import { z } from "zod";
import {
  STUDY_SCOPED_CAPABILITIES,
  type Capability,
  type ScopedPrincipal,
  type StudyScopedCapability,
} from "./capabilities";

/**
 * WHAT SOMEONE MAY DO INSIDE ONE STUDY, decided against membership rows.
 *
 * The profession matrix (`capabilityMatrix.ts`) does not apply here at all: it
 * says who may OPEN a study, and from then on a study's rights are its own
 * data. So a doctor invited read-only cannot enrol, even though enrolling is
 * ordinary work in a study of their own — rights inside someone else's
 * protocol are the stewards' to hand over, never a property of a job title.
 */

/** Just enough of a study for these rules. `createdByUserId` is still on the
 *  row and still non-null, but it is PROVENANCE now, not authority. */
export type StudyRef = {
  id: string;
  /** PRIVATE unless stated, so a caller that forgets to select the column gets
   *  the closed answer rather than an open one. */
  visibility?: "PRIVATE" | "PUBLIC";
};

/**
 * OWNER stewards the study: its people, its audience, its protocol and its
 * lifecycle. MEMBER does exactly what its capabilities say and nothing about
 * the study itself. A study always keeps at least one active OWNER — the API
 * refuses to remove or demote the last — which is what makes ownership
 * survivable where an immutable creator id was not.
 */
export const studyMemberRoleSchema = z.enum(["OWNER", "MEMBER"]);
export type StudyMemberRole = z.infer<typeof studyMemberRoleSchema>;

/**
 * One membership row, as the rules need it.
 *
 * `userId` and `revokedAt` are here so the rules match them THEMSELVES rather
 * than trusting every loader to have selected the right rows. Belt and braces,
 * and this is the one place a loader bug would read as an authorization
 * decision.
 */
export type MembershipLike = {
  studyId: string;
  userId: string;
  role: StudyMemberRole;
  capabilities: Capability[];
  revokedAt: Date | null;
};

/**
 * What a steward may hand to a MEMBER.
 *
 * `MANAGE_STUDIES` is deliberately absent: editing a study, activating and
 * closing it are stewardship, so they follow the OWNER role rather than a
 * capability anyone could be handed. Delegating them means promoting a
 * co-owner, which is a decision with a name rather than a checkbox.
 */
const ASSIGNABLE: readonly StudyScopedCapability[] = [
  "VIEW_STUDY",
  "ENROLL_PATIENTS",
  "TRIGGER_SURVEYS",
];
/** The same list widened, for callers holding a `Capability` to test against. */
export const STUDY_ASSIGNABLE_CAPABILITIES: readonly Capability[] = ASSIGNABLE;

/** Every MEMBER row carries `VIEW_STUDY`, added here if a caller left it out:
 *  belonging to a study you cannot see is not worth representing. */
export function withStudyVisibility(capabilities: Capability[]): Capability[] {
  return capabilities.includes("VIEW_STUDY")
    ? capabilities
    : ["VIEW_STUDY", ...capabilities];
}

/**
 * What a row STORES, given its role.
 *
 * An OWNER stores NOTHING: their rights follow the role, which
 * `studyCapability` reads directly, so a stored list would be a snapshot of
 * the vocabulary as of the day they were added — decisions staying correct
 * while the row rots. An empty array beside `role: OWNER` reads as "the role
 * speaks", which is what it means.
 */
export function capabilitiesForRole(
  role: StudyMemberRole,
  requested: Capability[],
): Capability[] {
  return role === "OWNER" ? [] : withStudyVisibility(requested);
}

/** The caller's own ACTIVE membership of this study, or nothing. */
function membershipOn(
  memberships: MembershipLike[],
  studyId: string,
  userId: string,
): MembershipLike | undefined {
  return memberships.find(
    (m) => m.studyId === studyId && m.userId === userId && m.revokedAt === null,
  );
}

/** Whether a row lets its holder SEE the study. Shared by `canSeeStudy` and
 *  the SQL-shaped `visibleStudyFilter`, so those cannot disagree about it. */
function membershipSees(membership: MembershipLike): boolean {
  return (
    membership.role === "OWNER" ||
    membership.capabilities.includes("VIEW_STUDY")
  );
}

/**
 * Whether this study exists AT ALL for this principal.
 *
 * Everyone else does not see the study, its cohort, its consent documents or
 * its scores, and the API answers 404 rather than 403 — a refusal would
 * confirm the protocol exists, which is the one thing not to leak.
 */
export function canSeeStudy(
  principal: ScopedPrincipal,
  study: StudyRef,
  memberships: MembershipLike[],
): boolean {
  // Visibility is not authority: `studyCapability` never consults it, so
  // PUBLIC means view-only for everyone who is not a member.
  if (study.visibility === "PUBLIC") return true;
  if (principal.isSuperAdmin || principal.isAdmin) return true;
  const membership = membershipOn(memberships, study.id, principal.id);
  return membership !== undefined && membershipSees(membership);
}

/**
 * The `where` fragment narrowing a study list to what this principal may see —
 * `canSeeStudy` in SQL, in plain object literals since this package must not
 * depend on the Prisma client.
 *
 * KEEP IT BESIDE THE PREDICATE. It is the one rule written twice in two
 * languages; the membership half is shared above, the PUBLIC and admin halves
 * are duplicated, and an integration test asserts the two agree over a seeded
 * mix. The failure it exists to catch is a 404 on a study the list just
 * displayed.
 */
export function visibleStudyFilter(
  principal: ScopedPrincipal,
  memberships: MembershipLike[],
): Record<string, unknown> {
  if (principal.isSuperAdmin || principal.isAdmin) return {};
  return {
    OR: [
      { visibility: "PUBLIC" },
      {
        id: {
          in: memberships
            .filter(
              (m) =>
                m.userId === principal.id &&
                m.revokedAt === null &&
                membershipSees(m),
            )
            .map((m) => m.studyId),
        },
      },
    ],
  };
}

/**
 * Whether this principal may perform `capability` ON THIS STUDY. The
 * membership row is authoritative.
 *
 * Throws on a capability that is not study-scoped, mirroring `hasCapability`.
 */
export function studyCapability(
  principal: ScopedPrincipal,
  capability: StudyScopedCapability,
  study: StudyRef,
  memberships: MembershipLike[],
): boolean {
  if (!STUDY_SCOPED_CAPABILITIES.includes(capability)) {
    throw new Error(
      `${capability} is not study-scoped: ask hasCapability(), not studyCapability().`,
    );
  }
  // Managing the study itself is stewardship: the role, never a row.
  if (capability === "MANAGE_STUDIES") {
    return canStewardStudy(principal, study, memberships);
  }
  if (principal.isSuperAdmin || principal.isAdmin) return true;
  const membership = membershipOn(memberships, study.id, principal.id);
  if (!membership) return false;
  // An OWNER holds every right in their own study — which is why their stored
  // list is empty. Fine-grained tuning is what MEMBER is for.
  return membership.role === "OWNER"
    ? ASSIGNABLE.includes(capability)
    : membership.capabilities.includes(capability);
}

/**
 * Who STEWARDS this study: an admin, or an OWNER. Deliberately a ROLE rather
 * than a capability, so it cannot be handed out by the mechanism it governs —
 * but transferable and multi-holder, so a study survives its founder.
 */
export function canStewardStudy(
  principal: ScopedPrincipal,
  study: StudyRef,
  memberships: MembershipLike[],
): boolean {
  if (principal.isSuperAdmin || principal.isAdmin) return true;
  return membershipOn(memberships, study.id, principal.id)?.role === "OWNER";
}

/** What the caller IS here, for a payload the client gates on rather than
 *  inferring from an id. */
export function viewerRoleIn(
  principal: ScopedPrincipal,
  study: StudyRef,
  memberships: MembershipLike[],
): StudyMemberRole | null {
  return membershipOn(memberships, study.id, principal.id)?.role ?? null;
}

/**
 * What this principal may hand to someone else here: what they hold
 * themselves. It does not bind today — only owners and admins reach the route
 * and both hold everything — but it keeps escalation closed if stewardship is
 * ever loosened to someone holding less.
 */
export function assignableCapabilities(
  principal: ScopedPrincipal,
  study: StudyRef,
  memberships: MembershipLike[],
): Capability[] {
  return ASSIGNABLE.filter((capability) =>
    studyCapability(principal, capability, study, memberships),
  );
}
